// src/contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { api, memoryAuth, scheduleTokenRefresh, logout as authLogout } from './authClient';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate              = useNavigate();

  // ─── Logout ──────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    await authLogout(navigate);
    setUser(null);
  }, [navigate]);

  // ─── Vérifier la session au chargement via /auth/me ──────────────────────
  // Le cookie access_token est envoyé automatiquement par le navigateur
  const checkAuth = useCallback(async () => {
    try {
      // Lire le cookie user_role (non-HttpOnly, lisible par JS)
      const roleCookie = document.cookie
        .split('; ')
        .find(r => r.startsWith('user_role='))
        ?.split('=')[1];

      // Si pas de cookie role, pas de session active
      if (!roleCookie || roleCookie !== 'staff') {
        setLoading(false);
        return;
      }

      // Récupérer les infos utilisateur
      const response = await api.get('/auth/me');
      const userData = response.data;

      memoryAuth.set({
        csrf_token: document.cookie
          .split('; ')
          .find(r => r.startsWith('csrf_token='))
          ?.split('=')[1] || '',
        role: roleCookie,
        user: userData,
      });

      setUser(userData);
      scheduleTokenRefresh(logout);

    } catch {
      // Session invalide ou expirée
      memoryAuth.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // ─── Login ────────────────────────────────────────────────────────────────
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { role, user: userData, csrf_token } = response.data;

      // Seuls les staff accèdent au back-office
      if (role !== 'staff') {
        return { success: false, error: 'Access restricted to staff only' };
      }

      memoryAuth.set({ csrf_token, role, user: userData });
      setUser(userData);
      scheduleTokenRefresh(logout);

      return { success: true, role };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  // ─── Vérification niveau de permission ───────────────────────────────────
  const hasMinLevel = useCallback((level) => {
    return (user?.permissions?.level ?? 0) >= level;
  }, [user]);

  const getUserLevel = useCallback(() => {
    return user?.permissions?.level ?? 0;
  }, [user]);

  // ─── Init au montage ──────────────────────────────────────────────────────
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    hasMinLevel,
    getUserLevel,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};